import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { FullScreenProgress } from '@occc/ui-design-system';

import { RetailerNetworkMyStoresStoreV2Page } from './retailer-network-my-stores-store-v2-page.component';

import {
  useGetRetailerNetworkLocationDetails,
  useGetRetailerNetworkLocationGroups,
} from '../../data-access/queries';
import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';
import { routeCreator } from '../retailer-connect.utils';
import { RetailerNetworkErrorMessage } from '../components/retailer-network-error-message.component';
import { useRetailerNetworkUserSelector } from '../retailer-network-context/use-retailer-network-user.selector';
import { RetailerNetworkNoData } from '../components/retailer-network-no-data.component';

export const RetailerNetworkMyStoresStoreV2PageContainer = () => {
  const { networkId = '', locationId = '' } = useParams<{
    networkId: string;
    locationId: string;
  }>();
  const {
    isRetailerNetworkSiteUserRole,
    retailerNetworkUserUuid,
    retailerNetworkUserOpenCommerceUserUuid,
  } = useRetailerNetworkUserSelector();

  const userScopingFilter =
    isRetailerNetworkSiteUserRole && retailerNetworkUserUuid
      ? {
          retailerNetworkUserUuidEquals: retailerNetworkUserUuid,
          openCommerceUserUuidEquals: retailerNetworkUserOpenCommerceUserUuid ?? undefined,
        }
      : {};

  const [searchParams] = useSearchParams();
  const groupRef = searchParams.get('groupRef');
  const groupName = searchParams.get('groupName');
  const {
    data: retailerNetworkLocationData,
    loading,
    error,
  } = useGetRetailerNetworkLocationDetails({
    filter: {
      retailerNetworkUuidEquals: networkId,
      ...userScopingFilter,
      retailerNetworkLocationUuidEquals: locationId,
    },
  });

  const location = retailerNetworkLocationData?.[0]?.location;
  const locationName = location?.name || location?.siteSystemConfiguration?.[0]?.identifier;

  const { data: locationGroups } = useGetRetailerNetworkLocationGroups({
    filter: {
      retailerNetworkUuidEquals: networkId,
      ...userScopingFilter,
      retailerNetworkLocationUuidEquals: locationId,
    },
  });
  const isLocationGroupsLoading = locationGroups === undefined;
  const currentLocationGroup = locationGroups?.[0] ?? null;
  const groupLabel = isLocationGroupsLoading ? groupName : currentLocationGroup?.name;
  const groupUuid = isLocationGroupsLoading ? groupRef : currentLocationGroup?.uuid;

  const groupSuffix =
    groupUuid && groupLabel
      ? `?groupRef=${groupUuid}&groupName=${encodeURIComponent(groupLabel)}`
      : '';

  const breadcrumbLinks = useMemo(
    () => [
      { label: 'My Stores', to: routeCreator.myStores(networkId) },
      ...(groupUuid && groupLabel
        ? [{ label: groupLabel, to: routeCreator.locationGroupDetails(networkId, groupUuid) }]
        : []),
      { label: locationName || 'Store Details' },
    ],
    [locationName, networkId, groupUuid, groupLabel],
  );

  useBreadcrumbLinks(breadcrumbLinks);

  if (loading && !location) {
    return <FullScreenProgress />;
  }

  if (error) {
    return <RetailerNetworkErrorMessage />;
  }

  if (!location) {
    return <RetailerNetworkNoData />;
  }

  return (
    <RetailerNetworkMyStoresStoreV2Page
      location={location}
      networkUuid={networkId}
      editDetailsTo={routeCreator.locationUpdate(networkId, locationId) + groupSuffix}
    />
  );
};
